import React, { useContext, useState, useCallback, useEffect } from "react";
import { FilterContext } from "../../context/Filter";
import {
  lobListComplaints,
  plListComplaints,
  pplListComplaints,
  problemAreaListComplaints,
  stateListComplaints,
  yearListComplaints,
} from "../../pages/Complaints/complaintsFilterKpi";

const ComplaintFilter = () => {
  const {
    arn: parArn,
    childArn,
    complaintsFilterPayload,
    setComplaintsFilterPayload,
    complaintsPayload,
    setComplaintsPayload,
  } = useContext(FilterContext);

  const [states, setStates] = useState([]);
  const [years, setYears] = useState([]);
  const [problemAreaListData, setProblemAreaListData] = useState([]);
  const [lob, setLob] = useState([]);
  const [ppl, setPpl] = useState([]);
  const [pl, setPl] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedStates, setSelectedStates] = useState("");
  const [selectedProblemArea, setSelectedProblemArea] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedLob, setSelectedLob] = useState("");
  const [selectedPpl, setSelectedPpl] = useState("");
  const [selectedPl, setSelectedPl] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedServiceType, setSelectedServiceType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Static options for the new dropdowns
  const statusOptions = [
    { key: "Open", label: "Open" },
    { key: "Closed", label: "Closed" },
  ];

  const serviceTypeOptions = [
    { key: "Sales", label: "Sales" },
    { key: "Service", label: "Service" },
  ];

  useEffect(() => {
    if (complaintsFilterPayload) {
      setSelectedStates(complaintsFilterPayload.states?.[0] || "");
      setSelectedProblemArea(complaintsFilterPayload.problem_area?.[0] || "");
      setSelectedYear(complaintsFilterPayload.years?.[0] || "");
      setSelectedLob(complaintsFilterPayload.lob?.[0] || "");
      setSelectedPpl(complaintsFilterPayload.ppl?.[0] || "");
      setSelectedPl(complaintsFilterPayload.pl?.[0] || "");
      setSelectedStatus(complaintsFilterPayload.status || "");
      setSelectedServiceType(complaintsFilterPayload.serviceType || "");
    }
  }, [complaintsFilterPayload]);

  const generatePayload = useCallback(() => {
    const payload = {
      ...(childArn ? { childArn } : { parArn }),
    };

    if (selectedStates) payload.states = [selectedStates];
    if (selectedYear) payload.years = [selectedYear];
    if (selectedProblemArea) payload.problem_area = [selectedProblemArea];
    if (selectedLob) payload.lob = [selectedLob];
    if (selectedPpl) payload.ppl = [selectedPpl];
    if (selectedPl) payload.pl = [selectedPl];
    if (selectedStatus) payload.status = selectedStatus;
    if (selectedServiceType) payload.serviceType = selectedServiceType;

    return payload;
  }, [
    parArn,
    childArn,
    selectedStates,
    selectedProblemArea,
    selectedYear,
    selectedLob,
    selectedPpl,
    selectedPl,
    selectedStatus,
    selectedServiceType,
  ]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (isSubmitting) return;

      setIsSubmitting(true);
      const payload = generatePayload();
      setComplaintsFilterPayload(payload);
      setComplaintsPayload(payload);

      setTimeout(() => setIsSubmitting(false), 800);
    },
    [
      generatePayload,
      isSubmitting,
      setComplaintsFilterPayload,
      setComplaintsPayload,
    ]
  );

  const handleReset = useCallback(
    (e) => {
      if (e?.preventDefault) e.preventDefault();

      const resetPayload = childArn ? { childArn } : { parArn };

      setSelectedStates("");
      setSelectedProblemArea("");
      setSelectedYear("");
      setSelectedLob("");
      setSelectedPpl("");
      setSelectedPl("");
      setSelectedStatus("");
      setSelectedServiceType("");

      setComplaintsFilterPayload(resetPayload);
      setComplaintsPayload(resetPayload);
    },
    [parArn, childArn, setComplaintsFilterPayload, setComplaintsPayload]
  );

  const fetchFilters = useCallback(async () => {
    setLoading(true);
    const payload = generatePayload();
    try {
      const [stateData, yearData, problemAreas, lobData, pplData, plData] =
        await Promise.all([
          stateListComplaints(parArn, childArn, payload),
          yearListComplaints(parArn, childArn, payload),
          problemAreaListComplaints(parArn, childArn, payload),
          lobListComplaints(parArn, childArn, payload),
          pplListComplaints(parArn, childArn, payload),
          plListComplaints(parArn, childArn, payload),
        ]);

      setStates(stateData || []);
      setYears(yearData || []);
      setProblemAreaListData(problemAreas || []);
      setLob(lobData || []);
      setPpl(pplData || []);
      setPl(plData || []);
    } catch (err) {
      console.error("Failed to fetch complaint filters", err);
    } finally {
      setLoading(false);
    }
  }, [parArn, childArn, generatePayload]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  if (loading) {
    return (
      <div className="max-w-full mx-auto p-6 bg-white rounded-md shadow-md flex items-center justify-center">
        <div className="text-gray-500">Loading filters...</div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-full mx-auto p-1 mt-2 mb-1 bg-white rounded-md shadow-md flex flex-wrap gap-2"
    >
      <Dropdown
        label="Status"
        value={selectedStatus}
        onChange={setSelectedStatus}
        options={statusOptions}
      />
      <Dropdown
        label="Complaint Area"
        value={selectedServiceType}
        onChange={setSelectedServiceType}
        options={serviceTypeOptions}
      />
      <Dropdown
        label="State"
        value={selectedStates}
        onChange={setSelectedStates}
        options={states}
      />
      <Dropdown
        label="ProblemArea"
        value={selectedProblemArea}
        onChange={setSelectedProblemArea}
        options={problemAreaListData}
      />
      <Dropdown
        label="Year"
        value={selectedYear}
        onChange={setSelectedYear}
        options={years}
      />
      <Dropdown
        label="LOB"
        value={selectedLob}
        onChange={setSelectedLob}
        options={lob}
      />
      <Dropdown
        label="PPL"
        value={selectedPpl}
        onChange={setSelectedPpl}
        options={ppl}
      />
      <Dropdown
        label="PL"
        value={selectedPl}
        onChange={setSelectedPl}
        options={pl}
      />

      {/* BUTTONS: Next line, centered */}
      <div className="w-full flex justify-center gap-x-4 ">
        <button
          type="submit"
          disabled={isSubmitting}
          className={`px-4 py-1 rounded-md transition ${
            isSubmitting
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-[#2374BF] hover:bg-indigo-700"
          } text-white`}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </button>

        <button
          type="button"
          onClick={handleReset}
          disabled={isSubmitting}
          className="bg-gray-400 text-white px-4 py-1 rounded-md hover:bg-gray-700 transition disabled:cursor-not-allowed"
        >
          Reset
        </button>
      </div>
    </form>
  );
};

const Dropdown = React.memo(({ label, value, onChange, options }) => {
  return (
    <div className="flex gap-2 items-center text-xs">
      <label>{label}:</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-gray-800 rounded-md py-1 px-2"
      >
        <option value="">Select {label}</option>
        {options?.map((item) => (
          <option key={item.key || item.value} value={item.key || item.value}>
            {item.key || item.label}
          </option>
        ))}
      </select>
    </div>
  );
});

Dropdown.displayName = "Dropdown";

export default ComplaintFilter;
